export type RealtimeEvent<T = unknown> = {
  type: string;
  payload: T;
  createdAt?: string;
};

type RealtimeClientOptions = {
  path?: string;
  reconnect?: boolean;
  reconnectDelayMs?: number;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (event: Event) => void;
  onEvent?: (event: RealtimeEvent) => void;
};

type RealtimeClient = {
  close: () => void;
  send: (type: string, payload?: unknown) => void;
};

function getApiBase() {
  return import.meta.env.VITE_API_URL || '/api/v1';
}

function getToken(): string | null {
  if (sessionStorage.getItem('ac_impersonate') === 'true') {
    return sessionStorage.getItem('ac_token');
  }
  return localStorage.getItem('ac_token');
}

function resolveRealtimeUrl(path: string) {
  const base = getApiBase().replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const token = getToken();

  if (base.startsWith('http://') || base.startsWith('https://')) {
    const url = new URL(`${base}${normalizedPath}`);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    if (token) url.searchParams.set('token', token);
    return url.toString();
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = new URL(`${base}${normalizedPath}`, `${protocol}//${window.location.host}`);
  if (token) url.searchParams.set('token', token);
  return url.toString();
}

export function createRealtimeClient(options: RealtimeClientOptions = {}): RealtimeClient {
  const {
    path = '/realtime',
    reconnect = true,
    reconnectDelayMs = 2500,
    onOpen,
    onClose,
    onError,
    onEvent,
  } = options;

  let socket: WebSocket | null = null;
  let reconnectTimer: number | null = null;
  let closedByCaller = false;

  const clearReconnect = () => {
    if (reconnectTimer !== null) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const connect = () => {
    clearReconnect();
    socket = new WebSocket(resolveRealtimeUrl(path));

    socket.addEventListener('open', () => {
      onOpen?.();
      socket?.send(JSON.stringify({ type: 'PING', payload: { source: 'web' } }));
    });

    socket.addEventListener('message', (message) => {
      try {
        const event = JSON.parse(message.data) as RealtimeEvent;
        if (event?.type) onEvent?.(event);
      } catch {
        // Ignore invalid realtime frames from the network edge.
      }
    });

    socket.addEventListener('error', (event) => {
      onError?.(event);
    });

    socket.addEventListener('close', () => {
      onClose?.();
      if (!closedByCaller && reconnect) {
        reconnectTimer = window.setTimeout(connect, reconnectDelayMs);
      }
    });
  };

  connect();

  return {
    close: () => {
      closedByCaller = true;
      clearReconnect();
      socket?.close();
      socket = null;
    },
    send: (type, payload = {}) => {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type, payload }));
      }
    },
  };
}
